/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { SummaryOfDetailedInfo } from "./info";
import { SummaryOfDetailedService } from "services/summary-of-detailed";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  accountNumber: "",
  monthYear: (today.getMonth() + 1).toString() + "/" + today.getFullYear().toString(),
  details: [
    {
      ordinalNumber: "01",
      name: "",
      openingBalance: 0,
      inwardAmount: 0,
      outwardAmount: 0,
      closingBalance: 0,
    }
  ],
  totalOpeningBalance: 0,
  totalInwardAmount: 0,
  totalOutwardAmount: 0,
  totalClosingBalance: 0,
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function SummaryOfDetailedDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await SummaryOfDetailedService.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            accountNumber: apiData.accountNumber || "",
            monthYear: apiData.monthYear || "",
            details: apiData.details?.length > 0 ? apiData.details : initialValues.details,
            totalOpeningBalance: apiData.totalOpeningBalance || 0,
            totalInwardAmount: apiData.totalInwardAmount || 0,
            totalOutwardAmount: apiData.totalOutwardAmount || 0,
            totalClosingBalance: apiData.totalClosingBalance || 0,
            reportDate: apiData.reportDate || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/summary-of-detailed");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        accountNumber: fValues.accountNumber,
        monthYear: fValues.monthYear,
        details: (fValues.details || []).map(row => ({
          ...row,
          openingBalance: num(row.openingBalance),
          inwardAmount: num(row.inwardAmount),
          outwardAmount: num(row.outwardAmount),
          closingBalance: num(row.closingBalance),
        })),
        totalOpeningBalance: num(fValues.totalOpeningBalance),
        totalInwardAmount: num(fValues.totalInwardAmount),
        totalOutwardAmount: num(fValues.totalOutwardAmount),
        totalClosingBalance: num(fValues.totalClosingBalance),
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await SummaryOfDetailedService.create(payload);
        toast.success("Tạo bảng tổng hợp thành công!");
      } else {
        await SummaryOfDetailedService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/summary-of-detailed");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S11-DN</title>
      <style>@media print{@page{size:A4 portrait;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <SummaryOfDetailedInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Bảng Tổng Hợp S11-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default SummaryOfDetailedDetail;
